import React, { ReactNode } from "react";
import { Scoped } from "kremling";

import { CpSelect } from "../cp-select/cp-select.component";
import {
  defaultSearchFilter,
  filterAlpha,
  SelectDataItem,
} from "../cp-select/common/utils";
import styles from "./../cp-select/cp-select.styles.scss";

export type CpSelectMultiProps<TData, TValue> = {
  allowTextWrap?: boolean;
  appendTo?: HTMLElement | "parent";
  className?: string;
  clearOnSelect?: boolean;
  contentWidth?: number | "sm" | "md" | "lg" | "block" | "auto";
  data?: TData[];
  disabled?: boolean;
  error?: boolean | string;
  groupType?: "header" | "border";
  insertSearch?: boolean;
  isGroupData?: boolean;
  loading?: boolean;
  onBlur?: () => void;
  onChange: (value: NoInfer<TValue>[]) => void;
  onClose?: () => void;
  onOpen?: () => void;
  placeholder?: string;
  preventKeyOpen?: boolean;
  preventOutsideClickUntilClosed?: boolean;
  renderFooter?: (params: any) => ReactNode;
  renderTrigger?: (props: any) => ReactNode;
  renderValue?: (value: SelectDataItem[]) => ReactNode;
  searchFilter?: boolean | ((item: any) => boolean);
  searchOnChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchValue?: string;
  stopPropagation?: boolean;
  transformData?: (item: TValue) => SelectDataItem;
  triggerIsBlock?: boolean;
  triggerRef?: React.RefObject<any>;
  value: TValue[];
};

function CpSelectMultiBase<TData, TValue>(
  props: CpSelectMultiProps<TData, TValue>,
) {
  const {
    allowTextWrap,
    appendTo,
    className,
    clearOnSelect,
    contentWidth,
    data,
    disabled,
    error,
    groupType,
    insertSearch,
    isGroupData,
    loading,
    onBlur,
    onChange,
    onClose,
    onOpen,
    placeholder = "Select",
    preventKeyOpen,
    preventOutsideClickUntilClosed,
    renderFooter,
    renderTrigger,
    renderValue,
    searchFilter,
    searchOnChange,
    searchPlaceholder,
    searchValue,
    stopPropagation,
    transformData,
    triggerIsBlock,
    triggerRef,
    value,
  } = props;
  return (
    <Scoped css={styles}>
      <CpSelect<TData, TValue>
        allowTextWrap={allowTextWrap}
        appendTo={appendTo}
        className={className}
        contentWidth={contentWidth}
        data={data}
        disabled={disabled}
        insertSearch={insertSearch}
        isGroupData={isGroupData}
        groupType={groupType}
        loading={loading}
        onChange={onChange as (value: TValue | TValue[]) => void}
        onClose={onClose}
        onOpen={onOpen}
        preventKeyOpen={preventKeyOpen}
        preventOutsideClickUntilClosed={preventOutsideClickUntilClosed}
        renderFooter={renderFooter || CpSelect.Footer}
        renderGroup={CpSelect.Group}
        renderItem={CpSelect.ItemMulti}
        renderTrigger={
          renderTrigger ||
          ((triggerProps) => {
            const TriggerComponent = CpSelect.TriggerMulti;
            return (
              <TriggerComponent
                {...triggerProps}
                onBlur={onBlur}
                placeholder={placeholder}
                renderValue={renderValue}
                error={error}
              />
            );
          })
        }
        searchFilter={defaultSearchFilter(searchFilter, searchValue)}
        searchOnChange={searchOnChange}
        searchPlaceholder={searchPlaceholder}
        searchValue={searchValue}
        clearOnSelect={clearOnSelect}
        showResultsInContent
        stopPropagation={stopPropagation}
        transformData={transformData}
        triggerIsBlock={triggerIsBlock}
        triggerRef={triggerRef}
        value={value}
      />
      {typeof error === "string" && (
        <div className="cp-select--error-text">{error}</div>
      )}
    </Scoped>
  );
}

// Export type that includes static method
export type CpSelectMultiComponent = (<TData, TValue>(
  props: CpSelectMultiProps<TData, TValue>,
) => React.JSX.Element) & {
  filterAlpha: typeof filterAlpha;
};

// Create typed component with static properties
const CpSelectMulti = CpSelectMultiBase as CpSelectMultiComponent;

// Attach static method
CpSelectMulti.filterAlpha = filterAlpha;

// Export the properly typed component
export { CpSelectMulti };
